<template>
  <div v-if="isOpen" class="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
    <div class="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-5xl w-full max-h-[75vh] overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300">
      <!-- Header -->
      <div class="relative bg-dark-teal-700 p-8 text-white">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="hidden md:block w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"></path>
              </svg>
            </div>
            <div>
              <h2 class="text-3xl font-bold">{{ t('contentStudio.title') }}</h2>
              <p class="text-white/80 text-sm">{{ t('contentStudio.subtitle') }}</p>
            </div>
          </div>
          <button @click="closeModal" class="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center backdrop-blur-sm transition-all duration-200 hover:scale-105">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <!-- Decorative elements -->
        <div class="absolute top-4 right-20 w-2 h-2 bg-white/40 rounded-full animate-pulse"></div>
        <div class="absolute bottom-4 left-20 w-1 h-1 bg-white/30 rounded-full animate-pulse delay-300"></div>
      </div>

      <!-- Content -->
      <div class="p-8 space-y-10 overflow-y-auto max-h-[calc(60vh)]">
        <!-- Product Information -->
        <div class="space-y-4">
          <div class="hidden md:block flex items-center gap-3">
            <div class="w-8 h-8 bg-coral-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900">{{ t('contentStudio.sections.productInfo.title') }}</h3>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.productInfo.fields.brand.label') }}</label>
              <input 
                v-model="contentData.brand" 
                type="text" 
                :placeholder="t('contentStudio.sections.productInfo.fields.brand.placeholder')"
                class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
              />
            </div>
            
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.productInfo.fields.title.label') }}</label>
              <input 
                v-model="contentData.productTitle" 
                type="text" 
                :placeholder="t('contentStudio.sections.productInfo.fields.title.placeholder')"
                class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
              />
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.productInfo.fields.status.label') }}</label>
            <select 
              v-model="contentData.status" 
              class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
            >
              <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                {{ t(status.labelKey) }}
              </option>
            </select>
          </div>

          <!-- Theme Toggle -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.productInfo.fields.theme.label') }}</label>
            <select 
              v-model="contentData.theme" 
              class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
            >
              <option v-for="theme in themeOptions" :key="theme.value" :value="theme.value">
                {{ t(theme.labelKey) }}
              </option>
            </select>
          </div>

          <!-- Background Color Picker -->
          <div class="space-y-2">
            <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.productInfo.fields.background.label') }}</label>
            <div class="flex gap-2">
              <input 
                type="color"
                v-model="contentData.backgroundColor"
                @input="handleBackgroundColorChange"
                class="w-16 h-12 border border-gray-200 rounded-xl cursor-pointer"
              />
              <input 
                type="text"
                v-model="contentData.backgroundColor"
                @input="handleBackgroundColorChange"
                :placeholder="t('contentStudio.sections.productInfo.fields.background.placeholder')"
                class="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white font-mono text-sm"
              />
            </div>
            <p class="text-xs text-gray-500">{{ t('contentStudio.sections.productInfo.fields.background.helper') }}</p>
          </div>
        </div>

        <!-- Product Images -->
        <div class="space-y-6">
          <div class="flex items-center gap-3">
            <div class="hidden md:block w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-2xl font-bold text-gray-900">{{ t('contentStudio.sections.images.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('contentStudio.sections.images.subtitle') }}</p>
            </div>
          </div>
          
          <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div 
              v-for="(image, index) in contentData.productImages" 
              :key="index"
              class="group relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 hover:border-coral-300 transition-all duration-300 hover:scale-105 cursor-move"
              draggable="true"
              @dragstart="handleImageDragStart($event, index)"
              @dragover.prevent
              @drop="handleImageDrop($event, index)"
            >
              <img v-if="image.url" :src="image.url" :alt="t('contentStudio.sections.images.imageAlt', { index: index + 1 })" class="w-full h-full object-cover" />
              <div v-else class="w-full h-full flex flex-col items-center justify-center text-gray-400 group-hover:text-coral-500 transition-colors">
                <svg class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                <span class="text-xs font-medium">{{ t('contentStudio.common.upload') }}</span>
              </div>
              
              <!-- Drag Handle Overlay -->
              <div class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div class="bg-black/50 backdrop-blur-sm rounded-lg p-1.5">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
                  </svg>
                </div>
              </div>
              
              <!-- Image Number Badge -->
              <div class="absolute top-2 left-2 bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1">
                <span class="text-white text-xs font-medium">{{ index + 1 }}</span>
              </div>
              <input 
                type="file" 
                accept="image/*" 
                @change="handleImageUpload($event, index)"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <button 
                v-if="image.url"
                @click="removeImage(index)"
                class="absolute top-3 right-3 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:scale-110 transition-all duration-200 backdrop-blur-sm"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <!-- Paired Products -->
        <div class="space-y-6">
          <div class="flex items-center gap-3">
            <div class="hidden md:block w-8 h-8 bg-light-brown-100 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-light-brown-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-2xl font-bold text-gray-900">{{ t('contentStudio.sections.paired.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('contentStudio.sections.paired.subtitle') }}</p>
            </div>
          </div>
          
          <div class="space-y-4">
            <div 
              v-for="(pair, index) in contentData.pairedProducts" 
              :key="index"
              class="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
              :class="{ 'cursor-move': !pair.isExpanded }"
              draggable="true"
              @dragstart="handleDragStart($event, index)"
              @dragover.prevent
              @drop="handleDrop($event, index)"
            >
              <!-- Collapsed Header (always visible) -->
              <div class="flex items-center justify-between p-6">
                <div class="flex items-center gap-4">
                  <!-- Drag Handle -->
                  <div class="cursor-move text-gray-400 hover:text-gray-600 transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
                    </svg>
                  </div>
                  
                  <!-- Product Info -->
                  <div class="flex-1">
                    <h4 class="font-semibold text-gray-900 text-lg">
                      {{ pair.title || t('contentStudio.sections.paired.defaultTitle', { index: index + 1 }) }}
                    </h4>
                    <div class="flex items-center gap-4 mt-1">
                      <span v-if="pair.price && pair.price > 0" class="text-coral-600 font-medium">
                        €{{ pair.price }}
                      </span>
                      <span v-else class="text-gray-400 text-sm">{{ t('contentStudio.sections.paired.noPrice') }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="flex items-center gap-2">
                  <!-- Expand/Collapse Button -->
                  <button 
                    @click="togglePairExpansion(index)"
                    class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  >
                    <svg 
                      class="w-5 h-5 transition-transform duration-200" 
                      :class="{ 'rotate-180': pair.isExpanded }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  
                  <!-- Remove Button -->
                  <button 
                    @click="removePairedProduct(index)"
                    class="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    {{ t('contentStudio.sections.paired.buttons.remove') }}
                  </button>
                </div>
              </div>
              
              <!-- Expanded Content (collapsible) -->
              <div 
                v-if="pair.isExpanded" 
                class="px-6 pb-6 space-y-6 border-t border-gray-100"
              >
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.paired.fields.title.label') }}</label>
                  <input 
                    v-model="pair.title" 
                    type="text" 
                    :placeholder="t('contentStudio.sections.paired.fields.title.placeholder')"
                    class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                  />
                </div>
                
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.paired.fields.url.label') }}</label>
                  <input 
                    v-model="pair.url" 
                    type="url" 
                    :placeholder="t('contentStudio.sections.paired.fields.url.placeholder')"
                    class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                  />
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.paired.fields.description.label') }}</label>
                  <textarea 
                    v-model="pair.description" 
                    :placeholder="t('contentStudio.sections.paired.fields.description.placeholder')"
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white resize-none"
                  ></textarea>
                </div>
                
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.paired.fields.price.label') }}</label>
                  <input 
                    v-model.number="pair.price" 
                    type="number" 
                    min="0"
                    step="0.01"
                    :placeholder="t('contentStudio.sections.paired.fields.price.placeholder')"
                    class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                  />
                  <p class="text-xs text-gray-500">{{ t('contentStudio.sections.paired.fields.price.helper') }}</p>
                </div>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-semibold text-gray-800">{{ t('contentStudio.sections.paired.fields.image.label') }}</label>
                <div class="relative w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 hover:border-coral-300 transition-all duration-300 group">
                  <img v-if="pair.imageUrl" :src="pair.imageUrl" :alt="t('contentStudio.sections.paired.imageAlt', { index: index + 1 })" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex flex-col items-center justify-center text-gray-400 group-hover:text-coral-500 transition-colors">
                    <svg class="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    <span class="text-xs font-medium">{{ t('contentStudio.common.upload') }}</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    @change="handlePairedImageUpload($event, index)"
                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
          
          <button 
            v-if="contentData.pairedProducts.length < 3"
            @click="addPairedProduct"
            class="w-full py-4 px-6 border-2 border-dashed border-gray-200 rounded-2xl text-gray-600 hover:border-coral-300 hover:text-coral-500 hover:bg-coral-50/50 transition-all duration-300 font-medium group"
          >
            <div class="flex items-center justify-center gap-2">
              <svg class="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {{ t('contentStudio.sections.paired.buttons.add') }}
            </div>
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="bg-gray-50/50 backdrop-blur-sm border-t border-gray-200 p-6">
        <div class="flex items-center justify-between">
          <div class="flex gap-2">
            <button 
              @click="clearStorage"
              class="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              {{ t('contentStudio.footer.clear') }}
            </button>
            <button 
              @click="debugData"
              class="px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              {{ t('contentStudio.footer.debug') }}
            </button>
          </div>
          <div class="flex gap-3">
            <button 
              @click="closeModal"
              class="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all duration-200 font-medium"
            >
              {{ t('contentStudio.footer.cancel') }}
            </button>
            <button 
              @click="saveContent"
              :disabled="isSaving"
              class="px-8 py-3 bg-gradient-to-r from-dark-teal-500 to-dark-teal-600 hover:from-dark-teal-600 hover:to-dark-teal-700 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
            >
              <div class="flex items-center gap-2">
                <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                {{ isSaving ? t('contentStudio.footer.saving') : t('contentStudio.footer.save') }}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { useI18n } from '#imports'
import { useContentStudio } from '~/composables/useContentStudio'
import { useGlobalTheme } from '~/composables/useGlobalTheme'

const isOpen = ref(false)
const isSaving = ref(false)

const { t } = useI18n()

const statusOptions = [
  { value: 'unpublished', labelKey: 'common.status.unpublished' },
  { value: 'discarded', labelKey: 'common.status.discarded' },
  { value: 'defected', labelKey: 'common.status.defected' },
  { value: 'light-defected', labelKey: 'common.status.lightDefected' }
]

const themeOptions = [
  { value: 'day', labelKey: 'contentStudio.sections.productInfo.fields.theme.options.day' },
  { value: 'night', labelKey: 'contentStudio.sections.productInfo.fields.theme.options.night' }
]

// Use the shared composable for reactive data
const { contentData, loadContentData, saveContentData, clearContentData, getThemeFromColor } = useContentStudio()

// Get global theme for synchronization
const { globalTheme, getSystemTheme, getEffectiveTheme } = useGlobalTheme()

// Watch for theme changes to reset background color
watch(() => contentData.theme, (newTheme, oldTheme) => {
  // Only reset if theme was manually changed (not from color picker)
  if (oldTheme && newTheme !== oldTheme) {
    contentData.backgroundColor = newTheme === 'night' ? '#000000' : '#ffffff'
  }
})

// Watch for changes in contentData and emit custom events for real-time updates
watch(contentData, (newData) => {
  // Emit custom event to notify other components of changes
  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('content-studio-updated', { 
      detail: newData 
    }))
  }
}, { deep: true })

// Synchronize Content Studio theme with Global Theme
watch(() => contentData.theme, (newTheme, oldTheme) => {
  if (newTheme && globalTheme.value !== newTheme) {
    // Use nextTick to ensure the update happens after the current tick
    nextTick(() => {
      // Update global theme to match Content Studio theme
      globalTheme.value = newTheme
      // Save to localStorage to override system preference
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('global-theme', newTheme)
      }
    })
  }
}, { immediate: true })

// Synchronize Global Theme with Content Studio theme
watch(globalTheme, (newGlobalTheme, oldGlobalTheme) => {
  if (newGlobalTheme && contentData.theme !== newGlobalTheme) {
    // Use nextTick to ensure the update happens after the current tick
    nextTick(() => {
      // Update Content Studio theme to match global theme
      contentData.theme = newGlobalTheme
    })
  }
}, { immediate: true })


// Save data using the composable
const saveContent = async () => {
  try {
    isSaving.value = true
    await saveContentData(contentData)
    closeModal()
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    if (message.includes('quota')) {
      alert(t('contentStudio.alerts.quota'))
    } else {
      console.error('Error saving content:', error)
      alert(t('contentStudio.alerts.saveError'))
    }
  } finally {
    isSaving.value = false
  }
}

// Clear localStorage data using composable
const clearStorage = () => {
  clearContentData()
  alert(t('contentStudio.alerts.cleared'))
}

// Debug function to check data
const debugData = () => {
  alert(t('contentStudio.alerts.debug', { count: contentData.productImages.length }))
}

// Modal controls
const openModal = () => {
  isOpen.value = true
  // Always load data to ensure we have the latest from localStorage or defaults
  loadContentData()
  
  // If no theme is set in Content Studio, initialize with system theme
  if (!contentData.theme) {
    const systemTheme = getSystemTheme()
    contentData.theme = systemTheme
    contentData.backgroundColor = systemTheme === 'night' ? '#000000' : '#ffffff'
  }
}

const closeModal = () => {
  isOpen.value = false
}

// Image handling
const handleImageUpload = (event, index) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      contentData.productImages[index].url = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const removeImage = (index) => {
  contentData.productImages[index].url = ''
}

// Paired products handling
const addPairedProduct = () => {
  if (contentData.pairedProducts.length < 3) {
    contentData.pairedProducts.push({
      title: '',
      url: '',
      description: '',
      price: null,
      imageUrl: '',
      isExpanded: false // New products start closed
    })
  }
}

// Handle background color change and auto-adjust theme
const handleBackgroundColorChange = () => {
  // Auto-adjust theme based on background color brightness
  const autoTheme = getThemeFromColor(contentData.backgroundColor)
  contentData.theme = autoTheme
}

// Drag and drop functionality for paired products
const draggedIndex = ref(null)

const handleDragStart = (event, index) => {
  draggedIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
  event.target.style.opacity = '0.5'
}

const handleDrop = (event, dropIndex) => {
  event.preventDefault()
  if (draggedIndex.value === null || draggedIndex.value === dropIndex) {
    return
  }
  
  // Reorder the array
  const draggedItem = contentData.pairedProducts[draggedIndex.value]
  contentData.pairedProducts.splice(draggedIndex.value, 1)
  contentData.pairedProducts.splice(dropIndex, 0, draggedItem)
  
  // Reset drag state
  draggedIndex.value = null
  event.target.style.opacity = '1'
}

// Drag and drop functionality for product images
const draggedImageIndex = ref(null)

const handleImageDragStart = (event, index) => {
  draggedImageIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
  event.target.style.opacity = '0.5'
}

const handleImageDrop = (event, dropIndex) => {
  event.preventDefault()
  if (draggedImageIndex.value === null || draggedImageIndex.value === dropIndex) {
    return
  }
  
  // Reorder the array
  const draggedItem = contentData.productImages[draggedImageIndex.value]
  contentData.productImages.splice(draggedImageIndex.value, 1)
  contentData.productImages.splice(dropIndex, 0, draggedItem)
  
  // Reset drag state
  draggedImageIndex.value = null
  event.target.style.opacity = '1'
}

// Expand/collapse functionality
const togglePairExpansion = (index) => {
  contentData.pairedProducts[index].isExpanded = !contentData.pairedProducts[index].isExpanded
}

const removePairedProduct = (index) => {
  contentData.pairedProducts.splice(index, 1)
}

const handlePairedImageUpload = (event, index) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      contentData.pairedProducts[index].imageUrl = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

// Expose methods for parent component
defineExpose({
  openModal,
  closeModal,
  contentData
})

// Load data only when modal is first opened, not on component mount
</script>
